import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http'
import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http'
import { injectable } from '@patsnap/tahiti-core/shared/inversify'
import type {
    DrugCardData,
    TargetCardData,
    OrganizationCardData,
    DiseaseCardData,
    ClinicalTrialCardData,
} from '../types'

export interface EntityCardApi {
    fetchDrugCard(id: string, isLogin: boolean): Promise<DrugCardData>
    fetchTargetCard(id: string, isLogin: boolean): Promise<TargetCardData>
    fetchOrganizationCard(id: string, isLogin: boolean): Promise<OrganizationCardData>
    fetchDiseaseCard(id: string, isLogin: boolean): Promise<DiseaseCardData>
    fetchNewsCard(id: string, isLogin: boolean): Promise<any>
    fetchPatentCard(id: string, isLogin: boolean): Promise<any>
    fetchPaperCard(id: string, isLogin: boolean): Promise<any>
    fetchClinicalTrialCard(id: string, isLogin: boolean): Promise<ClinicalTrialCardData>
    fetchClinicalResultsCard(id: string, isLogin: boolean): Promise<any>
    fetchDrugDealCard(id: string, isLogin: boolean): Promise<any>
}

export const EntityCardApi = Symbol('EntityCardApi')

@injectable()
export class EntityCardApiImpl extends PsHttpService implements EntityCardApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/synapse-api/pharmsnap_webapi/1.0`,
            baseURLGuest: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0/guest`,
        }
    }

    private getCardBaseURL(isLogin: boolean): string {
        return isLogin ? this.options.baseURL : this.options.baseURLGuest
    }

    async fetchDrugCard(id: string, isLogin: boolean): Promise<DrugCardData> {
        return this.get<PatSnapCommonResponseData<DrugCardData>>({
            url: `${this.getCardBaseURL(isLogin)}/drug/card/${id}`,
        }).then(res => res as unknown as DrugCardData)
    }

    async fetchTargetCard(id: string, isLogin: boolean): Promise<TargetCardData> {
        return this.get<PatSnapCommonResponseData<TargetCardData>>({
            url: `${this.getCardBaseURL(isLogin)}/target/card/${id}`,
        }).then(res => res as unknown as TargetCardData)
    }

    async fetchOrganizationCard(id: string, isLogin: boolean): Promise<OrganizationCardData> {
        return this.get<PatSnapCommonResponseData<OrganizationCardData>>({
            url: `${this.getCardBaseURL(isLogin)}/organization/card/${id}`,
        }).then(res => res as unknown as OrganizationCardData)
    }

    async fetchDiseaseCard(id: string, isLogin: boolean): Promise<DiseaseCardData> {
        return this.get<PatSnapCommonResponseData<DiseaseCardData>>({
            url: `${this.getCardBaseURL(isLogin)}/disease/card/${id}`,
        }).then(res => res as unknown as DiseaseCardData)
    }

    async fetchNewsCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({
            url: `${this.getCardBaseURL(isLogin)}/news/card/${id}`,
        })
    }

    async fetchPatentCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({
            url: `${this.getCardBaseURL(isLogin)}/patent/card/${id}`,
        })
    }

    async fetchPaperCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({
            url: `${this.getCardBaseURL(isLogin)}/paper/card/${id}`,
        })
    }

    async fetchClinicalTrialCard(id: string, isLogin: boolean): Promise<ClinicalTrialCardData> {
        return this.get<PatSnapCommonResponseData<ClinicalTrialCardData>>({
            url: `${this.getCardBaseURL(isLogin)}/${isLogin ? 'clinical-trial' : 'clinical_trial'}/card/${id}`,
        }).then(res => res as unknown as ClinicalTrialCardData)
    }

    async fetchClinicalResultsCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({
            url: `${this.getCardBaseURL(isLogin)}/ct_result/card/${id}`,
        })
    }

    async fetchDrugDealCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({
            url: `${this.getCardBaseURL(isLogin)}/drug_deal/card/${id}`,
        })
    }
}
